import moment from 'moment';
import 'moment/locale/sk';
import 'moment/locale/hu';
import reframe from 'nike-re-framejs';
import { merge } from 'lodash';
import { AxiosInstance } from 'axios';
import { Dialog, NuiDialogProps } from '../components/dialogs/dialogs';
import { NikeCmsDomain } from '../types/generated/nike-cms-domain';
import { NavigateNikeRoute, NikeRoute, RouteParams, SearchParams } from './routing/types';
import { History } from 'history';

export declare type NuiPlatform = 'portal' | 'mobile';
window.reframe = reframe;
moment.updateLocale('sk', { relativeTime: { future: 'o %s' } });
moment.locale(window.OPTIONS.locale || window.OPTIONS.lang);
moment.suppressDeprecationWarnings = true;

let __isNuixCtxInitialized = false;
let __nuiCtx = {
    platform: 'mobile',
    routing: {
        routes: null,
        redirects: null,
        navigate: (
            route: NavigateNikeRoute,
            params?: RouteParams,
            searchParams?: SearchParams,
            state?: History.LocationState,
            allowReplace?: boolean
        ) => {
            console.error(`Accessing property 'nuiCtx.routing.navigate' without setting it first`);
        },
        history_back: (defaultRoute?: NikeRoute, params?: RouteParams) => {
            console.error(`Accessing property 'nuiCtx.routing.history_back' without setting it first`);
        },
        updateURLSearchParams: (
            searchParams: SearchParams,
            state?: History.LocationState,
            replace: boolean = false
        ) => {
            console.error(`Accessing property 'nuiCtx.routing.updateURLSearchParams' without setting it first`);
        }
    },
    user: {
        isloggedIn: false
    },
    pushClient: {
        subscription: function({
            onSnapshot,
            onMessage,
            onFailure
        }: {
            onSnapshot?: (data?: any) => void;
            onMessage: (data?: any) => void;
            onFailure: (error) => void;
        }) {
            console.warn('subscripiton not implemented');
            return {
                resetPaths: function(paths) {
                    console.warn('resetPaths not implemented');
                },
                resetPushPaths: function(paths) {
                    console.warn('resetPushPaths not implemented');
                },
                close: function() {
                    console.warn('close not implemented');
                },
                reload: function() {
                    console.warn('reload not implemented');
                }
            };
        }
    },
    openDialog: <DialogProps extends any>(popup: Dialog<DialogProps>, data: DialogProps) => {
        console.error(`Accessing property 'nuiCtx.openDialog' without setting it first`);
    },
    closeDialog: <DialogProps extends any>(popup: Dialog<DialogProps>) => {
        console.error(`Accessing property 'nuiCtx.closeDialog' without setting it first`);
    },
    closeAllDialogs: () => {
        console.error(`Accessing property 'nuiCtx.closeDialog' without setting it first`);
    },
    openLoginDialog: (title: string) => {
        console.error(`Accessing property 'nuiCtx.openLoginDialog' without setting it first`);
    },
    openLoginPage: () => {
        console.error(`Accessing property 'nuiCtx.openLoginPage' without setting it first`);
    },
    components: {
        NuiDialog: (props: NuiDialogProps): JSX.Element => {
            console.error(`Accessing property 'nuiCtx.components.NuiDialog' without setting it first`);
            return null;
        },
        MarkdownArticle: (niceUrl: string): JSX.Element => {
            console.error(`Accessing property 'nuiCtx.components.MarkdownArticle' without setting it first`);
            return null;
        },
        PrematchDetail: (boxStore: string, boxId: string, matchId: string): JSX.Element => {
            console.error(`Accessing property 'nuiCtx.components.PrematchDetal' without setting it first`);
            return null;
        },
        BetbookPostItem: (
            postType: string,
            post: any,
            detail?: boolean,
            header?: boolean,
            onClick?: () => void
        ): JSX.Element => {
            console.error(`Accessing property 'nuiCtx.components.BetbookPostItem' without setting it first`);
            return null;
        },
        BetbookTopEvents: (): JSX.Element => {
            console.error(`Accessing property 'nuiCtx.components.BetbookTopEvents' without setting it first`);
            return null;
        }
    },
    getArticle: async (niceUrl: string): Promise<NikeCmsDomain.MarkdownArticleDto> => {
        console.error(`Accessing property 'nuiCtx.getArticle' without setting it first`);
        return Promise.reject(niceUrl + ' was not found');
    },
    services: {
        nikeone: undefined as AxiosInstance
    },
    properties: {
        'cake-cms.image.link.url': 'THIS SHOULD BE OVERRIDDEN',
        'static.url': 'THIS SHOULD BE OVERRIDDEN',
        'cakeCms.endpoint.url': 'THIS SHOULD BE OVERRIDDEN',
        'fe-dwh.endpoint.url': 'THIS SHOULD BE OVERRIDDEN'
    },
    handlers: {
        cashinOfferPush: message => {
            console.error(`Accessing handler 'cashinOfferPush' without setting it first`);
        },
        cashinOfferSnapshot: messages => {
            console.error(`Accessing handler 'cashinOfferSnapshot' without setting it first`);
        },
        cashinOfferFailure: error => {
            console.error(`Accessing handler 'cashinOfferFailure' without setting it first`);
        }
    },
    casino: {
        'casino.notificationByFun.timeout': 60000,
        'casino.image.link.url': 'THIS SHOULD BE OVERRIDDEN',
        lotteryPlayGame: (gameId: string, mode: string) => {
            console.error(`Accessing handler 'lotteryPlayGame' without setting it first`);
        }
    }
};

export const initNuiCtx = (config: Partial<typeof __nuiCtx>): typeof __nuiCtx => {
    __isNuixCtxInitialized = true;

    __nuiCtx = merge(__nuiCtx, config);
    return __nuiCtx;
};

const nuiCtxProxy = () =>
    new Proxy(__nuiCtx, {
        get: (obj, prop) => {
            const _props = String(prop);
            if (!__isNuixCtxInitialized) {
                throw Error(
                    `

Accessing property 'nuiCtx.${_props}' without calling initNuiCtx first.

You have probably used nuiCtx in file on top level.
This is wrong, because usage on top level would be evaluated during parse time,
which is much sooner, than initNuiCtx() could have been called. 
This cannot be prevented trivially prevented.

Consider accesing nuiCtx in lazy evaluation - instead of:
'const a = nuiCtx.a' use 'const getA = () => nuiCtx.a'

`
                );
            }
            return obj[prop];
        }
    });

export const nuiCtx = process.env.NODE_ENV === 'production' ? __nuiCtx : nuiCtxProxy();
